import {
  AIPromptEventName,
  EventName,
} from 'features/cantileverAISystem/eventName'
import z from 'zod'

type Event = {
  eventName: EventName
}

export type EventDefinition = {
  eventName: EventName
  eventSchema: z.ZodType<Event>
}

// Allow <TEvent extends Event>(event: TEvent) => void to be assigned to (event: Event) => void
// Function parameters in Typescript are "contravariant", meaning typescript will normally reject this assignment
// Methods in Typescript are "bivariant", which is less strict and allows this assignment
// https://www.typescriptlang.org/docs/handbook/type-compatibility.html#function-parameter-bivariance
// Alternative solution is to write (event: any) => void in the eventNameToHandlers dictionary
export type EventHandler<TEvent extends Event> = {
  bivarianceHack(event: TEvent): void
}['bivarianceHack']

export class EventBus {
  private eventNameToHandlers: Partial<
    Record<EventName, ((event: Event) => void)[]>
  > = {}

  public subscribeAllAIPromptEvent<TEvent extends Event>(
    handler: EventHandler<TEvent>
  ) {
    for (const eventName of Object.values(AIPromptEventName)) {
      if (!this.eventNameToHandlers[eventName]) {
        this.eventNameToHandlers[eventName] = []
      }

      const upcastedHandler: EventHandler<Event> = handler
      if (!this.eventNameToHandlers[eventName].includes(upcastedHandler)) {
        this.eventNameToHandlers[eventName].push(upcastedHandler)
      }
    }
  }

  public subscribe<TEventDef extends EventDefinition>(
    eventDef: TEventDef,
    handler: EventHandler<z.infer<TEventDef['eventSchema']>>
  ) {
    const eventName = eventDef.eventName
    if (!this.eventNameToHandlers[eventName]) {
      this.eventNameToHandlers[eventName] = []
    }
    // Widen the handler's typing by forgetting the generic so we can insert into the dictionary
    const upcastedHandler: EventHandler<Event> = handler
    if (!this.eventNameToHandlers[eventName].includes(upcastedHandler)) {
      this.eventNameToHandlers[eventName].push(upcastedHandler)
    }
  }

  public unsubscribe(eventName: EventName, handler: EventHandler<Event>) {
    if (!this.eventNameToHandlers[eventName]) {
      return
    }
    this.eventNameToHandlers[eventName] = this.eventNameToHandlers[
      eventName
    ].filter((h) => h !== handler)
  }

  public send<TEvent extends Event>(event: TEvent) {
    this.eventNameToHandlers[event.eventName]?.forEach((handler) => {
      handler(event)
    })
  }

  public clear() {
    this.eventNameToHandlers = {}
  }
}
